<template>
  <!-- Mobile: full-screen stepped flow; desktop: single modal. Both share the
       same form state, submit path, and success UX. -->
  <AddLanguageSteps
    v-if="isMobile && modelValue"
    :picked-language="form.pickedLanguage.value"
    :picked-reason="form.pickedReason.value"
    :picked-proficiency="form.pickedProficiency.value"
    :reasons="form.reasons.value"
    :proficiency-levels="form.proficiencyLevels.value"
    :native-language-code="form.nativeLanguageCode.value"
    :held-codes="form.heldCodes.value"
    :active-code="form.activeLanguageCode.value"
    :error-key="form.errorKey.value"
    :is-submitting="form.isSubmitting.value"
    @pick-language="form.pickedLanguage.value = $event"
    @pick-reason="form.pickedReason.value = $event"
    @pick-proficiency="form.pickedProficiency.value = $event"
    @close="onCancel"
    @submit="submitForm"
  />
  <!-- Modal only emits false (dismissal); route it through the same
       cancel+reset path as the mobile flow so reopening starts clean. -->
  <Modal
    v-else
    :model-value="modelValue"
    :title="$t('addLanguage.title')"
    :dialog-class="'modal-dialog-centered modal-dialog-scrollable'"
    @update:model-value="onCancel"
  >
    <div class="form-section">
      <h6 class="section-title">{{ $t('onboarding.newLanguage.selectLanguage') }}</h6>
      <LanguagePickerGrid
        :selected-code="form.pickedLanguage.value"
        :disabled-base-codes="form.nativeLanguageCode.value ? [form.nativeLanguageCode.value] : []"
        :disabled-codes="form.heldCodes.value"
        :active-code="form.activeLanguageCode.value"
        :show-other-card="false"
        @pick="selectLanguage"
      />
    </div>

    <div class="form-section" ref="reasonSection" data-test="reason-section">
      <h6 class="section-title">{{ $t('onboarding.newLanguage.whyLearning') }}</h6>
      <div class="reason-options">
        <div
          v-for="reason in form.reasons.value"
          :key="reason.key"
          :class="['reason-option', { active: form.pickedReason.value === reason.key }]"
          :data-test="'reason-' + reason.key"
          @click="selectReason(reason.key)"
        >
          {{ reason.label }}
        </div>
      </div>
    </div>

    <div class="form-section" ref="proficiencySection" data-test="proficiency-section">
      <h6 class="section-title">{{ $t('onboarding.newLanguage.proficiencyLevel') }}</h6>
      <div class="proficiency-options">
        <div
          v-for="level in form.proficiencyLevels.value"
          :key="level.key"
          :class="['proficiency-option', { active: form.pickedProficiency.value === level.key }]"
          :data-test="'proficiency-' + level.key"
          @click="selectProficiency(level.key)"
        >
          {{ level.label }}
        </div>
      </div>
    </div>

    <div v-if="form.errorKey.value" class="alert alert-danger" data-test="add-error">
      {{ $t(form.errorKey.value) }}
    </div>

    <template #footer>
      <button
        type="button"
        class="button submit wizard-btn"
        data-test="add-submit"
        :disabled="!form.isValid.value || form.isSubmitting.value"
        @click="submitForm"
      >
        <span v-if="form.isSubmitting.value" class="spinner-border spinner-border-sm me-2"></span>
        {{ $t('addLanguage.submit') }}
      </button>
    </template>
  </Modal>
</template>

<script>
import { ref as vueRef, getCurrentInstance, onUnmounted } from "vue";
import Modal from "@/components/Modal.vue";
import LanguagePickerGrid from "@/components/LanguagePickerGrid.vue";
import AddLanguageSteps from "@/components/AddLanguageSteps.vue";
import Languages from "@/data/Language.js";
import i18n from "@/locales";
import { useAddLanguageForm } from "@/composables/useAddLanguageForm.js";
import { useScrollToSection } from "@/composables/useScrollToSection.js";
import { useHaptics } from "@/composables/useHaptics.js";
import { useToast } from "@/composables/useToast.js";

export default {
  name: "AddLanguageFlow",
  components: { Modal, LanguagePickerGrid, AddLanguageSteps },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const form = useAddLanguageForm();
    const { notifySuccess } = useHaptics();
    const { showToast } = useToast();

    // Desktop stacked-modal auto-scroll (mobile uses the stepped AddLanguageSteps
    // flow, which is one question per screen and needs no scroll).
    const { scrollToSection, cancelScroll } = useScrollToSection();
    const reasonSection = vueRef(null);
    const proficiencySection = vueRef(null);

    const media = window.matchMedia("(max-width: 768px)");
    const isMobile = vueRef(media.matches);
    const onMediaChange = (e) => {
      // A desktop->mobile swap unmounts this modal; drop any pending scroll so
      // it can't fire against a section that's about to detach.
      cancelScroll();
      isMobile.value = e.matches;
    };
    media.addEventListener("change", onMediaChange);

    const selectLanguage = (code) => {
      form.pickedLanguage.value = code;
      // Skipped at fire time if reason is already chosen (e.g. re-tapping a flag).
      scrollToSection(reasonSection.value, () => !form.pickedReason.value);
    };
    const selectReason = (key) => {
      form.pickedReason.value = key;
      scrollToSection(
        proficiencySection.value,
        () => !form.pickedProficiency.value
      );
    };
    const selectProficiency = (key) => {
      form.pickedProficiency.value = key;
    };

    if (getCurrentInstance()) {
      onUnmounted(() => {
        media.removeEventListener("change", onMediaChange);
        cancelScroll();
      });
    }

    const close = () => emit("update:modelValue", false);

    const onCancel = () => {
      cancelScroll();
      close();
      form.reset();
    };

    const submitForm = async () => {
      // Cancel synchronously (before the await) so a pending scroll can't fire
      // during submission or against the closing modal — mirrors
      // OnboardingWizard.submitForm.
      cancelScroll();
      const pickedCode = form.pickedLanguage.value;
      if (!(await form.submit())) return;
      notifySuccess();
      showToast(
        i18n.global.t("addLanguage.success", {
          language: Languages[pickedCode]?.nativeName || pickedCode,
        }),
        "success"
      );
      close();
      form.reset();
    };

    return {
      form,
      isMobile,
      onCancel,
      submitForm,
      reasonSection,
      proficiencySection,
      selectLanguage,
      selectReason,
      selectProficiency,
    };
  },
};
</script>

<style scoped>
.form-section {
  margin-bottom: 1.5rem;
  /* Auto-scroll targets: keep the section heading clear of the sticky modal
     chrome after scrollIntoView({ block: 'start' }) lands (Unit 2). */
  scroll-margin-top: 1rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--al-text);
  margin-bottom: 0.75rem;
}

.reason-options,
.proficiency-options {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.reason-option,
.proficiency-option {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0.5rem 1rem;
  background-color: var(--al-surface-sunken);
  border-radius: var(--al-radius-pill);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all var(--al-dur-short) var(--al-ease-move);
  border: 2px solid transparent;
}

.reason-option:hover,
.proficiency-option:hover {
  background-color: var(--al-border);
}

.reason-option.active,
.proficiency-option.active {
  background-color: var(--al-primary-soft);
  border-color: var(--al-primary);
  color: var(--al-text);
  font-weight: 500;
}

.button.submit {
  background-color: var(--al-primary);
  color: var(--al-white);
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: var(--al-radius);
  font-weight: 600;
  transition: all var(--al-dur-short) var(--al-ease-move);
}

.button.submit:hover:not(:disabled) {
  background-color: var(--al-primary-hover);
}

.button.submit:disabled {
  background-color: var(--al-light-blue);
  cursor: not-allowed;
}

.wizard-btn {
  min-width: 120px;
}
</style>
